import { UrlConfig } from 'config';
import { ArtistModel, ItemModel, PaginationModel, ListingModel, ArtistIndexModel, ItemBidModel, ItemAskModel } from 'models';
import { RawArtistData } from 'models/Artist/Artist';
import { ArtistIndexType, RawArtistIndex } from 'models/Artist/ArtistIndex';
import { RawItemData, RawItemActiveMarketData, ItemActiveMarketData } from 'models/Item/types';
import { RawListingData } from 'models/Listing/types';
import { RawPaginationData } from 'models/Pagination/Pagination';
import { ApiResponse } from 'models/Api/ApiResponse';
import { AjaxService } from 'services';
import { JSONObject } from 'models/Api/types';

export class ArtistApiClient {
  public static async getArtistById(id: string): Promise<ArtistModel> {
    const url = UrlConfig.ARTIST.ARTIST_BY_ID + `${id}`;
    const response: ApiResponse<RawArtistData> = await AjaxService.getData(url);
    const artist = new ArtistModel(response.data);
    return artist;
  }

  public static async getArtistByItemId(id: string): Promise<ArtistModel> {
    const url = UrlConfig.ARTIST.ARTIST_BY_ITEM + `${id}`;
    const response: ApiResponse<RawArtistData> = await AjaxService.getData(url);
    const artist = new ArtistModel(response.data);
    return artist;
  }

  public static async getAllArtists(): Promise<ArtistModel[]> {
    const url = UrlConfig.ARTIST.ARTIST_ALL;
    const response: ApiResponse<RawArtistData[]> = await AjaxService.getData(url);
    const artistList = response.data.map((rawArtist: RawArtistData) => new ArtistModel(rawArtist));
    return artistList;
  }

  public static async getArtistItems(id: string): Promise<ItemModel[]> {
    const url = UrlConfig.ARTIST.ARTIST_BY_ID + `${id}/items`;
    const response: ApiResponse<RawItemData[]> = await AjaxService.getData(url);
    const itemsList = response.data.map((rawItem: RawItemData) => new ItemModel(rawItem));
    return itemsList;
  }

  public static async getArtistListings(id: string, page: number): Promise<PaginationModel<ListingModel>> {
    let url = UrlConfig.ARTIST.ARTIST_BY_ID + `${id}/listings`;
    if (page) {
      url += `?page=${page}`;
    }
    const response: ApiResponse<RawPaginationData<RawListingData>> = await AjaxService.getData(url);
    const paginatedRespose: PaginationModel<RawListingData> = new PaginationModel(response.data);
    const formattedListings = paginatedRespose.data.map((rawListing: RawListingData) => new ListingModel(rawListing));
    const formattedPaginatedResponse: PaginationModel<ListingModel> = new PaginationModel({ ...response.data, data: formattedListings });
    return formattedPaginatedResponse;
  }

  public static async getArtistEditionIndex(id: string): Promise<ArtistIndexModel> {
    const url = UrlConfig.ARTIST.ARTIST_BY_ID + `${id}/index/edition`;
    const response: ApiResponse<RawArtistIndex> = await AjaxService.getData(url);
    const artistIndex = new ArtistIndexModel(response.data);
    return artistIndex
  }

  public static async getArtistActiveMarket(id: string): Promise<ItemActiveMarketData> {
    const url = UrlConfig.ARTIST.ARTIST_BY_ID + `${id}/active-market`;
    const response: ApiResponse<RawItemActiveMarketData> = await AjaxService.getData(url);
    const activeMarket = {
      bids: response.data?.bids.map(rawBid => new ItemBidModel(rawBid)),
      asks: response.data?.asks.map(rawAsk => new ItemAskModel(rawAsk))
    };
    return activeMarket;
  }

  public static async regenerateArtistIndex(artistId: string, type: ArtistIndexType): Promise<void> {
    const url = UrlConfig.ARTIST.INDEX_REGENERATE;
    await AjaxService.postData(url, {
      artist_id: artistId,
      type,
    });
  }

  public static async refreshArtistIndex(artistId: string, type: ArtistIndexType): Promise<void> {
    const url = UrlConfig.ARTIST.INDEX_REFRESH;
    await AjaxService.postData(url, {
      artist_id: artistId,
      type,
    });
  }

  public static async followArtist(id: string): Promise<void> {
    const url = UrlConfig.ARTIST.ARTIST_FOLLOW;
    await AjaxService.postData(url, {
      artist_id: id
    });
  }

  public static async unfollowArtist(id: string): Promise<void> {
    const url = UrlConfig.ARTIST.ARTIST_UNFOLLOW;
    await AjaxService.postData(url, {
      artist_id: id
    });
  }

  public static async updateArtist(id: string, data: JSONObject): Promise<ArtistModel> {
    const url = UrlConfig.ARTIST.ARTIST_UPDATE + `${id}/update`;
    const response: ApiResponse<RawArtistData> = await AjaxService.postData(url, data);
    return new ArtistModel(response.data);
  }

  public static async createArtist(data: JSONObject): Promise<string> {
    const url = UrlConfig.ARTIST.ARTIST_CREATE;
    const response: ApiResponse<string> = await AjaxService.postData(url, data);
    return response.data;
  }

  public static async migrateArtistImage(id: string, imageUrl: string): Promise<void> {
    const url = UrlConfig.ARTIST.ARTIST_MIGRATE_IMAGE;
    await AjaxService.postData(url, {
      artist_id: id,
      image: imageUrl
    });
  }
}
